import { EnumProcessorSchedulingStatus } from '../../../../lib/graphql';

export interface CancelBookingFormValues {
  reason: string;
  details: string;
  // Set when cancelling a recurring job and the scheduler opts to also end the
  // series + cancel its upcoming jobs.
  alsoCancelSeries?: boolean;
}

export const CANCELLABLE_STATUSES = [
  EnumProcessorSchedulingStatus.PendingDeposit,
  EnumProcessorSchedulingStatus.Initial,
  EnumProcessorSchedulingStatus.Requested,
  EnumProcessorSchedulingStatus.Scheduled,
  EnumProcessorSchedulingStatus.DroppedOff,
  EnumProcessorSchedulingStatus.Killed,
  EnumProcessorSchedulingStatus.Aging,
  EnumProcessorSchedulingStatus.Curing,
];

export const CUTSHEET_STATUSES = [
  EnumProcessorSchedulingStatus.DroppedOff,
  EnumProcessorSchedulingStatus.Requested,
  EnumProcessorSchedulingStatus.Scheduled,
  EnumProcessorSchedulingStatus.Killed,
  EnumProcessorSchedulingStatus.Aging,
  EnumProcessorSchedulingStatus.Curing,
];

export const STATUS_BADGE_COLORS: Record<string, { bg: string; text: string }> =
  {
    [EnumProcessorSchedulingStatus.Requested]: {
      bg: '#E3F2FD',
      text: '#1976D2',
    },
    [EnumProcessorSchedulingStatus.Scheduled]: {
      bg: '#E3F2FD',
      text: '#1976D2',
    },
    [EnumProcessorSchedulingStatus.MissedDropOff]: {
      bg: '#FFEBEE',
      text: '#C62828',
    },
    [EnumProcessorSchedulingStatus.DroppedOff]: {
      bg: '#E8F5E9',
      text: '#388E3C',
    },
    [EnumProcessorSchedulingStatus.Killed]: { bg: '#C8E6C9', text: '#2E7D32' },
    [EnumProcessorSchedulingStatus.Aging]: { bg: '#C8E6C9', text: '#2E7D32' },
    [EnumProcessorSchedulingStatus.Curing]: { bg: '#C8E6C9', text: '#2E7D32' },
    [EnumProcessorSchedulingStatus.ReadyPayAtPickup]: {
      bg: '#F3E5F5',
      text: '#7B1FA2',
    },
    [EnumProcessorSchedulingStatus.Invoicing]: {
      bg: '#FFF3E0',
      text: '#E65100',
    },
    [EnumProcessorSchedulingStatus.InvoicePaid]: {
      bg: '#F3E5F5',
      text: '#7B1FA2',
    },
    [EnumProcessorSchedulingStatus.Completed]: {
      bg: '#F5F5F5',
      text: '#616161',
    },
    [EnumProcessorSchedulingStatus.PendingDeposit]: {
      bg: '#FFF8E1',
      text: '#F57F17',
    },
  };

export const FALLBACK_BADGE_COLORS = { bg: '#F5F5F5', text: '#616161' };

/** Animal-only cancellation status — jobs never store it. */
export const ANIMAL_STATUS_CANCELLED = 'Cancelled';

export const getStatusBadgeColors = (status: string) => {
  if (status === ANIMAL_STATUS_CANCELLED) {
    return { bg: '#FFEBEE', text: '#C62828' };
  }
  return STATUS_BADGE_COLORS[status] ?? FALLBACK_BADGE_COLORS;
};

export const IN_PROGRESS_BADGE_COLORS = { bg: '#E8F5E9', text: '#2E7D32' };

/**
 * Canonical forward order of the status flow, in web enum space. Pre-facility
 * statuses are included so "ahead of current" comparisons work for animals
 * still mirroring a Requested/Scheduled job.
 */
export const STATUS_FLOW_ORDER = [
  EnumProcessorSchedulingStatus.Initial,
  EnumProcessorSchedulingStatus.Requested,
  EnumProcessorSchedulingStatus.Scheduled,
  EnumProcessorSchedulingStatus.DroppedOff,
  EnumProcessorSchedulingStatus.Killed,
  EnumProcessorSchedulingStatus.Aging,
  EnumProcessorSchedulingStatus.Curing,
  EnumProcessorSchedulingStatus.Invoicing,
  EnumProcessorSchedulingStatus.ReadyPayAtPickup,
  EnumProcessorSchedulingStatus.InvoicePaid,
  EnumProcessorSchedulingStatus.Completed,
];

/** Statuses a processor may set directly on an individual animal. */
export const ANIMAL_EDITABLE_STATUSES = [
  EnumProcessorSchedulingStatus.DroppedOff,
  EnumProcessorSchedulingStatus.Killed,
  EnumProcessorSchedulingStatus.Aging,
  EnumProcessorSchedulingStatus.Curing,
  EnumProcessorSchedulingStatus.Invoicing,
  EnumProcessorSchedulingStatus.ReadyPayAtPickup,
  EnumProcessorSchedulingStatus.InvoicePaid,
  EnumProcessorSchedulingStatus.Completed,
];

/** Cancelled/failure statuses — excluded from the divergence mask and never editable. */
export const CANCELLED_DISPLAY_STATUSES: string[] = [
  EnumProcessorSchedulingStatus.MissedDropOff,
  EnumProcessorSchedulingStatus.CancelledByProcessor,
  EnumProcessorSchedulingStatus.CancelledByProducer,
  EnumProcessorSchedulingStatus.DepositFailed,
  EnumProcessorSchedulingStatus.CheckoutExpired,
  EnumProcessorSchedulingStatus.DeniedByProcessor,
  ANIMAL_STATUS_CANCELLED,
];

/**
 * Statuses offered in the animal-search "Status" filter (flow order + the
 * animal-only Cancelled). Labels via renderStatusText; normalized server-side.
 */
export const ANIMAL_STATUS_FILTER_VALUES: string[] = [
  EnumProcessorSchedulingStatus.Requested,
  EnumProcessorSchedulingStatus.Scheduled,
  EnumProcessorSchedulingStatus.DroppedOff,
  EnumProcessorSchedulingStatus.Killed,
  EnumProcessorSchedulingStatus.Aging,
  EnumProcessorSchedulingStatus.Curing,
  EnumProcessorSchedulingStatus.Invoicing,
  EnumProcessorSchedulingStatus.ReadyPayAtPickup,
  EnumProcessorSchedulingStatus.InvoicePaid,
  EnumProcessorSchedulingStatus.Completed,
  ANIMAL_STATUS_CANCELLED,
];

/**
 * Map a status to web enum space. Accepts either the already-sanitized job enum
 * ("Dropped_Off") or the animal field's raw stored value ("Dropped Off"), since
 * sanitizing is idempotent.
 * @param raw
 */
export const normalizeStatusValue = (
  raw: string | null | undefined,
): string | undefined => (raw ? raw.replace(/[^_a-zA-Z0-9]/g, '_') : undefined);

/**
 * Forward-only dropdown options for an animal: editable statuses strictly ahead
 * of its current one. Empty for cancelled/unrecognized statuses.
 * @param rawStatus
 */
export const animalStatusOptions = (
  rawStatus: string | null | undefined,
): EnumProcessorSchedulingStatus[] => {
  const current = normalizeStatusValue(rawStatus);
  if (!current || CANCELLED_DISPLAY_STATUSES.includes(current)) return [];
  const idx = STATUS_FLOW_ORDER.indexOf(
    current as EnumProcessorSchedulingStatus,
  );
  if (idx === -1) return [];
  return ANIMAL_EDITABLE_STATUSES.filter(
    (status) => STATUS_FLOW_ORDER.indexOf(status) > idx,
  );
};

// `wouldSettleInvoicesOnMove` is DELETED (split-level invoicing & settlement,
// Phase 4): it predicted whether a per-animal Invoice-Paid move would trigger
// the backend's status-driven invoice settlement — a mechanism that no longer
// exists. A status change never settles invoices; money is recorded only
// through the explicit payment actions.

/**
 * True when a job's animals hold more than one distinct in-band status, so its
 * display should be masked as "In Progress" (purely presentational).
 *
 * Only facility-band statuses (Dropped Off … Completed) count: pre-facility
 * mixes (e.g. stale 'Initial' next to 'Requested') must NOT mask an
 * awaiting-acceptance job as green. Cancelled animals never count.
 * @param animalStatuses
 */
export const hasDivergentAnimalStatuses = (
  animalStatuses: Array<string | null | undefined>,
): boolean => {
  const inBand = new Set(
    animalStatuses
      .map(normalizeStatusValue)
      .filter(
        (status): status is EnumProcessorSchedulingStatus =>
          !!status &&
          ANIMAL_EDITABLE_STATUSES.includes(
            status as EnumProcessorSchedulingStatus,
          ),
      ),
  );
  return inBand.size > 1;
};

export const renderStatusText = (statusValue: string) => {
  if (statusValue === ANIMAL_STATUS_CANCELLED) return 'Cancelled';
  switch (statusValue) {
    case EnumProcessorSchedulingStatus.PendingDeposit:
      return 'Pending Deposit';
    case EnumProcessorSchedulingStatus.Requested:
    case EnumProcessorSchedulingStatus.Initial:
      return 'Requested';
    case EnumProcessorSchedulingStatus.Scheduled:
      return 'Scheduled';
    case EnumProcessorSchedulingStatus.MissedDropOff:
      return 'Missed Drop Off';
    case EnumProcessorSchedulingStatus.DroppedOff:
      return 'Dropped Off';
    case EnumProcessorSchedulingStatus.Killed:
      return 'Harvested';
    case EnumProcessorSchedulingStatus.Aging:
      return 'Aging';
    case EnumProcessorSchedulingStatus.Curing:
      return 'Curing';
    case EnumProcessorSchedulingStatus.Invoicing:
      return 'Invoicing';
    case EnumProcessorSchedulingStatus.ReadyPayAtPickup:
      return 'Ready for Pickup';
    case EnumProcessorSchedulingStatus.InvoicePaid:
      return 'Invoice Paid';
    case EnumProcessorSchedulingStatus.Completed:
      return 'Completed';
    case EnumProcessorSchedulingStatus.CancelledByProcessor:
      return 'Cancelled by Processor';
    case EnumProcessorSchedulingStatus.CancelledByProducer:
      return 'Cancelled by Producer';
    case EnumProcessorSchedulingStatus.DepositFailed:
      return 'Deposit Failed';
    case EnumProcessorSchedulingStatus.CheckoutExpired:
      return 'Checkout Expired';
    case EnumProcessorSchedulingStatus.DeniedByProcessor:
      return 'Denied';
    default:
      return 'Unknown';
  }
};

/**
 * Single source of truth for a job's status display: the masked "In Progress"
 * label/colors when animals diverge, else the normal status. EVERY job-status
 * render should go through this so displays can't drift.
 * @param job
 * @param job.status
 * @param job.animalHeads
 */
export const jobStatusDisplay = (job: {
  status?: string | null;
  animalHeads?: Array<{ status?: string | null } | null> | null;
}): {
  label: string;
  colors: { bg: string; text: string };
  masked: boolean;
} => {
  if (
    hasDivergentAnimalStatuses(
      (job.animalHeads ?? []).map((head) => head?.status),
    )
  ) {
    return {
      label: 'In Progress',
      colors: IN_PROGRESS_BADGE_COLORS,
      masked: true,
    };
  }
  return {
    label: job.status ? renderStatusText(job.status) : '',
    colors: getStatusBadgeColors(job.status ?? ''),
    masked: false,
  };
};

export enum EnumProcessorStatusGroups {
  Requested = 'Requested',
  Scheduled = 'Scheduled',
  In_Progress = 'In Progress',
  Completed = 'Completed',
}

export const ALL_SHOWN_STATUSES = [
  EnumProcessorSchedulingStatus.Requested,
  EnumProcessorSchedulingStatus.Scheduled,
  EnumProcessorSchedulingStatus.DroppedOff,
  EnumProcessorSchedulingStatus.Killed,
  EnumProcessorSchedulingStatus.Aging,
  EnumProcessorSchedulingStatus.Curing,
  EnumProcessorSchedulingStatus.Invoicing,
  EnumProcessorSchedulingStatus.ReadyPayAtPickup,
  EnumProcessorSchedulingStatus.InvoicePaid,
  EnumProcessorSchedulingStatus.Completed,
  EnumProcessorSchedulingStatus.MissedDropOff,
];

export const IN_PROGRESS_STATUSES = [
  EnumProcessorSchedulingStatus.DroppedOff,
  EnumProcessorSchedulingStatus.Killed,
  EnumProcessorSchedulingStatus.Aging,
  EnumProcessorSchedulingStatus.Curing,
  EnumProcessorSchedulingStatus.Invoicing,
  EnumProcessorSchedulingStatus.InvoicePaid,
  EnumProcessorSchedulingStatus.ReadyPayAtPickup,
  EnumProcessorSchedulingStatus.Scheduled,
];

export const COMPLETED_STATUSES = [
  EnumProcessorSchedulingStatus.MissedDropOff,
  EnumProcessorSchedulingStatus.Completed,
  EnumProcessorSchedulingStatus.CancelledByProcessor,
  EnumProcessorSchedulingStatus.CancelledByProducer,
  EnumProcessorSchedulingStatus.DepositFailed,
  EnumProcessorSchedulingStatus.CheckoutExpired,
  EnumProcessorSchedulingStatus.DeniedByProcessor,
];

export const getStatusColor = (statusValue: string) => {
  switch (statusValue) {
    case EnumProcessorSchedulingStatus.PendingDeposit:
    case EnumProcessorSchedulingStatus.Requested:
    case EnumProcessorSchedulingStatus.Initial:
      return 'statusRequested.main';

    case EnumProcessorSchedulingStatus.Scheduled:
      return 'statusScheduled.main';

    case EnumProcessorSchedulingStatus.DroppedOff:
    case EnumProcessorSchedulingStatus.Killed:
    case EnumProcessorSchedulingStatus.Aging:
    case EnumProcessorSchedulingStatus.Curing:
    case EnumProcessorSchedulingStatus.Invoicing:
    case EnumProcessorSchedulingStatus.ReadyPayAtPickup:
    case EnumProcessorSchedulingStatus.InvoicePaid:
      return 'statusInProgress.main';

    case EnumProcessorSchedulingStatus.MissedDropOff:
    case EnumProcessorSchedulingStatus.Completed:
    case EnumProcessorSchedulingStatus.CancelledByProcessor:
    case EnumProcessorSchedulingStatus.CancelledByProducer:
    case EnumProcessorSchedulingStatus.DepositFailed:
    case EnumProcessorSchedulingStatus.CheckoutExpired:
    case EnumProcessorSchedulingStatus.DeniedByProcessor:
      return 'statusCompleted.main';

    default:
      return 'statusCompleted.main';
  }
};

export const getGroupColor = (statusValue: string) => {
  switch (statusValue) {
    case EnumProcessorStatusGroups.Requested:
      return 'statusRequested.main';

    case EnumProcessorStatusGroups.Scheduled:
      return 'statusScheduled.main';

    case EnumProcessorStatusGroups.In_Progress:
      return 'statusInProgress.main';

    case EnumProcessorStatusGroups.Completed:
      return 'statusCompleted.main';

    default:
      return 'statusCompleted.main';
  }
};
